import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { fetchPortfoliosWithValues, createPortfolio, type PortfolioWithValue } from '../lib/data'

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

function Dashboard() {
  const { user: _user } = useAuth()
  const [portfolios, setPortfolios] = useState<PortfolioWithValue[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newPortfolioName, setNewPortfolioName] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    loadPortfolios()
  }, [])

  async function loadPortfolios() {
    try {
      setLoading(true)
      const data = await fetchPortfoliosWithValues()
      setPortfolios(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load portfolios')
    } finally {
      setLoading(false)
    }
  }

  async function handleCreatePortfolio(e: React.FormEvent) {
    e.preventDefault()
    if (!newPortfolioName.trim()) return

    setCreating(true)
    try {
      await createPortfolio(newPortfolioName.trim(), {
        accountType: 'GIA',
        currency: 'GBP',
        cashBalance: 0,
      })
      setNewPortfolioName('')
      setShowCreateModal(false)
      await loadPortfolios()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create portfolio')
    } finally {
      setCreating(false)
    }
  }

  const totalValue = portfolios.reduce((sum, p) => sum + p.totalValue, 0)
  const totalCost = portfolios.reduce((sum, p) => sum + p.costBasis, 0)
  const totalGain = totalValue - totalCost
  const totalGainPct = totalCost > 0 ? (totalGain / totalCost) * 100 : 0
  const totalDayChange = portfolios.reduce((sum, p) => sum + p.dayChange, 0)
  const previousDayValue = totalValue - totalDayChange
  const totalDayChangePct = previousDayValue > 0 ? (totalDayChange / previousDayValue) * 100 : 0
  const totalMtdChange = portfolios.reduce((sum, p) => sum + p.mtdChange, 0)
  const mtdBaseValue = totalValue - totalMtdChange
  const totalMtdChangePct = mtdBaseValue > 0 ? (totalMtdChange / mtdBaseValue) * 100 : 0
  const totalYtdChange = portfolios.reduce((sum, p) => sum + p.ytdChange, 0)
  const ytdBaseValue = totalValue - totalYtdChange
  const totalYtdChangePct = ytdBaseValue > 0 ? (totalYtdChange / ytdBaseValue) * 100 : 0

  if (loading) {
    return (
      <div className="app-container">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="app-container">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Error loading portfolios: {error}</p>
          <button 
            onClick={loadPortfolios}
            className="mt-2 text-sm text-red-600 underline"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="app-container">
      {/* Total Summary */}
      <div className="mb-6">
        <p className="text-sm text-gray-500 mb-1">Total Portfolio Value</p>
        <h1 className="text-3xl font-bold text-gray-900">{formatCurrency(totalValue)}</h1>
        
        {/* Period Changes Grid */}
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className={`text-center py-2 rounded-lg ${totalDayChange >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            <p className="text-xs text-gray-500 mb-0.5">Today</p>
            <p className="font-semibold text-sm">{formatCurrency(Math.abs(totalDayChange))}</p>
            <p className="text-xs">{totalDayChange >= 0 ? '+' : ''}{totalDayChangePct.toFixed(1)}%</p>
          </div>
          <div className={`text-center py-2 rounded-lg ${totalMtdChange >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            <p className="text-xs text-gray-500 mb-0.5">MTD</p>
            <p className="font-semibold text-sm">{formatCurrency(Math.abs(totalMtdChange))}</p>
            <p className="text-xs">{totalMtdChange >= 0 ? '+' : ''}{totalMtdChangePct.toFixed(1)}%</p>
          </div>
          <div className={`text-center py-2 rounded-lg ${totalYtdChange >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            <p className="text-xs text-gray-500 mb-0.5">YTD</p>
            <p className="font-semibold text-sm">{formatCurrency(Math.abs(totalYtdChange))}</p>
            <p className="text-xs">{totalYtdChange >= 0 ? '+' : ''}{totalYtdChangePct.toFixed(1)}%</p>
          </div>
        </div>
        
        <div className="text-sm text-gray-500 mt-3">
          Cost: {formatCurrency(totalCost)} · Gain: 
          <span className={totalGain >= 0 ? 'text-green-600' : 'text-red-600'}>
            {' '}{totalGain >= 0 ? '+' : ''}{formatCurrency(totalGain)} ({totalGainPct >= 0 ? '+' : ''}{totalGainPct.toFixed(1)}%)
          </span>
        </div>
      </div>

      {/* Portfolio Cards */}
      <div className="space-y-3 mb-8">
        {portfolios.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-lg mb-2">Welcome to KitScope! 🦊</p>
            <p className="text-sm mb-4">Create your first portfolio to get started.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              ＋ Create Portfolio
            </button>
          </div>
        ) : (
          portfolios.map((portfolio) => (
            <Link
              key={portfolio.id}
              to={`/portfolio/${portfolio.id}`}
              className="card block hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-semibold text-gray-900">{portfolio.name}</h2>
                <span className="text-lg font-bold">{formatCurrency(portfolio.totalValue)}</span>
              </div>
              <div className={`flex items-center gap-1 text-sm ${portfolio.dayChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                <span>{portfolio.dayChange >= 0 ? '▲' : '▼'}</span>
                <span>{formatCurrency(Math.abs(portfolio.dayChange))} today ({portfolio.dayChangePct >= 0 ? '+' : ''}{portfolio.dayChangePct.toFixed(1)}%)</span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                <span>Gain: {portfolio.gain >= 0 ? '+' : ''}{formatCurrency(Math.abs(portfolio.gain))} ({portfolio.gainPct >= 0 ? '+' : ''}{portfolio.gainPct.toFixed(1)}%)</span>
                <span>Cash: {formatCurrency(portfolio.cash_balance)}</span>
              </div>
            </Link>
          ))
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Link to="/add-trade" className="btn-primary flex-1 text-center">
          📊 Add Trade
        </Link>
        <Link to="/import" className="btn-secondary flex-1 text-center">
          📁 Import CSV
        </Link>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-secondary flex-1"
        >
          ＋ Portfolio
        </button>
      </div>

      {/* Create Portfolio Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create Portfolio</h2>
            <form onSubmit={handleCreatePortfolio}>
              <div className="mb-4">
                <label htmlFor="portfolioName" className="block text-sm font-medium text-gray-700 mb-1">
                  Portfolio Name
                </label>
                <input
                  type="text"
                  id="portfolioName"
                  value={newPortfolioName}
                  onChange={(e) => setNewPortfolioName(e.target.value)}
                  placeholder="e.g., Colin SIPP, Emma ISA"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                  disabled={creating}
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    setNewPortfolioName('')
                  }}
                  className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !newPortfolioName.trim()}
                  className="flex-1 py-2 px-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard
